import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useContent } from '../contexts/ContentContext';
import {
  findSpecialty,
  findCourse,
  getSpecialtyPath,
  resolveLegacyCoursePath,
} from '../utils/formationCatalog';
import { formationWhatsAppUrl } from '../utils/whatsapp';
import FormationSelect from './FormationSelect';
import CountryCityFields from './CountryCityFields';
import useHashScroll from '../hooks/useHashScroll';
import { resolveDefaultCountry } from '../utils/locationData';
import { getMediaUrl } from '../utils/mediaUrl';
import { getFormationReviewsDisplay, isVideoReview } from '../utils/formationTestimonials';
import TestimonialVideoBlock from './TestimonialVideoBlock';
import { filterUpcomingSessions } from '../utils/sessionDates';
import {
  getFormationContentId,
  trackContact,
  trackLeadIntent,
  prepareInscriptionSubmitTracking,
  trackCompleteRegistration,
  trackViewContent,
} from '../utils/metaPixel';

const FormationDetail = () => {
  const { specialtySlug, courseSlug } = useParams();
  const navigate = useNavigate();
  const { getContent } = useContent();
  const { scrollToElement } = useHashScroll();

  const specialty = specialtySlug ? findSpecialty(getContent, specialtySlug) : null;
  const formation = specialtySlug && courseSlug
    ? findCourse(getContent, specialtySlug, courseSlug)
    : null;
  const upcomingSessions = formation ? filterUpcomingSessions(formation.sessions || []) : [];

  const contactForm = getContent('contact.contactForm') || {};
  const registrationForm = getContent('formations.registrationForm') || {};
  const defaultCountry = resolveDefaultCountry(contactForm.defaultCountry);
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [cityKey, setCityKey] = useState(0);
  const [formData, setFormData] = useState({
    nom: '',
    email: '',
    telephone: '',
    country: defaultCountry,
    ville: '',
    formation: formation?.title || '',
    message: '',
  });
  const [formStatus, setFormStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (formation) return;

    const legacySlug = courseSlug || specialtySlug;
    if (legacySlug) {
      const legacyPath = resolveLegacyCoursePath(getContent, legacySlug);
      if (legacyPath && !legacyPath.endsWith(`/${legacySlug}`)) {
        navigate(legacyPath, { replace: true });
        return;
      }
    }

    navigate('/formations', { replace: true });
  }, [formation, specialtySlug, courseSlug, getContent, navigate]);

  useEffect(() => {
    if (formation) {
      setFormData((prev) => ({ ...prev, formation: formation.title }));
    }
  }, [formation]);

  useEffect(() => {
    if (!formation) return;
    const specialtyKey = specialty?.slug || specialty?.id || specialtySlug;
    trackViewContent({
      formationId: getFormationContentId(formation, specialtyKey),
      formationName: formation.title,
      specialtyName: specialty?.title,
    });
  }, [formation, specialty, specialtySlug]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (formStatus.message) {
      setFormStatus({ type: '', message: '' });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus({ type: '', message: '' });

    try {
      const API_BASE =
        process.env.REACT_APP_API_URL ||
        'https://dentoxcellence-backend.dentoxcellence.workers.dev/api';

      const specialtyKey = specialty?.slug || specialty?.id || specialtySlug;
      const metaTracking = prepareInscriptionSubmitTracking({
        formationId: getFormationContentId(formation, specialtyKey),
        formationName: formation?.title || formData.formation,
        source: 'formation_detail_form',
      });

      const response = await fetch(`${API_BASE}/inscription/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          country: formData.country || selectedCountry,
          ville: [formData.ville, formData.country || selectedCountry].filter(Boolean).join(', '),
          ...metaTracking,
        }),
      });

      const data = await response.json();

      if (data.success) {
        trackCompleteRegistration({
          eventId: metaTracking.metaCompleteRegistrationEventId,
          formationId: formationContentId,
          formationName: formation?.title || formData.formation,
          source: 'formation_detail_form',
        });
        setFormStatus({
          type: 'success',
          message:
            "Votre demande d'inscription a été envoyée avec succès ! Nous vous contacterons bientôt.",
        });
        setFormData({
          nom: '',
          email: '',
          telephone: '',
          country: selectedCountry,
          ville: '',
          formation: formation?.title || '',
          message: '',
        });
        setCityKey((k) => k + 1);
      } else {
        setFormStatus({
          type: 'error',
          message: data.message || "Erreur lors de l'envoi. Veuillez réessayer.",
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus({
        type: 'error',
        message: 'Erreur de connexion. Veuillez réessayer plus tard.',
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!formation) {
    return null;
  }

  const specialtyPathSlug = specialty?.slug || specialty?.id || specialtySlug;
  const formationContentId = getFormationContentId(formation, specialtyPathSlug);
  const whatsappUrl = formationWhatsAppUrl(formation, getContent);

  const handleSectionNav = (e, sectionId) => {
    e.preventDefault();
    scrollToElement(sectionId);
  };

  const handleWhatsAppClick = () => {
    trackContact({
      formationId: formationContentId,
      formationName: formation.title,
      source: 'formation_detail_whatsapp',
    });
  };

  const handleRegisterClick = (e) => {
    trackLeadIntent({
      formationId: formationContentId,
      formationName: formation.title,
      source: 'formation_detail_register_click',
    });
    handleSectionNav(e, 'inscription');
  };

  return (
    <div className="formations-page formation-detail-page" style={{ overflow: 'visible' }}>
      <header className="formation-detail-sticky-header">
        <nav className="formation-detail-breadcrumb-bar" aria-label="Fil d'Ariane">
          <div className="container">
            <div className="formation-detail-breadcrumb-inner">
              <Link to="/" className="formation-detail-breadcrumb-link">
                Accueil
              </Link>
              <span className="formation-detail-breadcrumb-sep" aria-hidden="true">
                ›
              </span>
              <Link to="/formations" className="formation-detail-breadcrumb-link">
                Formations
              </Link>
              {specialty && (
                <>
                  <span className="formation-detail-breadcrumb-sep" aria-hidden="true">
                    ›
                  </span>
                  <Link
                    to={getSpecialtyPath(specialtyPathSlug)}
                    className="formation-detail-breadcrumb-link"
                  >
                    {specialty.title}
                  </Link>
                </>
              )}
              <span className="formation-detail-breadcrumb-sep" aria-hidden="true">
                ›
              </span>
              <span className="formation-detail-breadcrumb-current">{formation.title}</span>
            </div>
          </div>
        </nav>

        <div className="formation-detail-action-bar" aria-label="Actions formation">
          <div className="container">
            <div className="formation-detail-action-inner">
              <a
                href={whatsappUrl}
                className="formation-detail-action-btn formation-detail-action-btn--whatsapp formation-detail-action-btn--primary-row"
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleWhatsAppClick}
              >
                <i className="fab fa-whatsapp" aria-hidden="true" />
                <span>Contactez-nous sur WhatsApp</span>
              </a>
              <div className="formation-detail-action-row">
                <a
                  href="#inscription"
                  className="formation-detail-action-btn formation-detail-action-btn--register"
                  onClick={handleRegisterClick}
                >
                  <i className="fas fa-user-plus" aria-hidden="true" />
                  <span>M&apos;inscrire</span>
                </a>
                <a
                  href="#avis"
                  className="formation-detail-action-btn formation-detail-action-btn--reviews"
                  onClick={(e) => handleSectionNav(e, 'avis')}
                >
                  <i className="fas fa-star" aria-hidden="true" />
                  <span>Avis ancien participant</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </header>
      <div className="formation-detail-breadcrumb-spacer" aria-hidden="true" />

      <section className="formations-grid-section formation-detail-main-section">
        <div className="container">
          <div className="formations-grid" style={{ display: 'flex', justifyContent: 'center' }}>
            <div
              className={`formation-card-detailed formation-${formation.color}`}
              style={{ maxWidth: '900px', width: '100%' }}
            >
              <div className="formation-header-detailed">
                <div
                  className="formation-photo"
                  style={{
                    position: 'relative',
                    width: '100%',
                    paddingBottom: formation.formationPhotoSettings?.aspectRatio
                      ? `${(1 /
                          (() => {
                            const ratios = {
                              '1:1': 1,
                              '4:3': 4 / 3,
                              '16:9': 16 / 9,
                              '4:5': 4 / 5,
                              '3:2': 3 / 2,
                              '21:9': 21 / 9,
                            };
                            return (
                              ratios[formation.formationPhotoSettings.aspectRatio] || 16 / 9
                            );
                          })()) *
                        100}%`
                      : '56.25%',
                    overflow: 'hidden',
                  }}
                >
                  {formation.formationPhoto ? (
                    <img
                      src={getMediaUrl(formation.formationPhoto)}
                      alt={formation.title}
                      style={
                        formation.formationPhotoSettings
                          ? {
                              transform: `translate(calc(-50% + ${formation.formationPhotoSettings.position.x}px), calc(-50% + ${formation.formationPhotoSettings.position.y}px)) scale(${formation.formationPhotoSettings.scale})`,
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              minWidth: '100%',
                              minHeight: '100%',
                              width: 'auto',
                              height: 'auto',
                              objectFit: 'cover',
                            }
                          : {}
                      }
                    />
                  ) : (
                    <div className="formation-photo-placeholder">
                      <i className={formation.icon} />
                    </div>
                  )}
                </div>

                <div className="formation-title-section">
                    <h3>{formation.title}</h3>
                  <p className="formation-subtitle">{formation.subtitle}</p>

                  {(formation.level || []).length > 0 && (
                    <div className="formation-levels">
                      <i className="fas fa-layer-group" aria-hidden="true" />
                      {(formation.level || []).map((level, levelIndex) => (
                        <span key={levelIndex} className="level-badge">{level}</span>
                      ))}
                    </div>
                  )}

                  {upcomingSessions.length > 0 && (
                    <div className="formation-spots-display">
                      <i className="fas fa-users" />
                      <span className="spots-text">{upcomingSessions[0].spots}</span>
                      <span className="next-session">
                        Prochaine session: {upcomingSessions[0].date}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {(formation.instructor || formation.instructorBio || formation.instructorPhoto) && (
                <div className="formation-instructor-section">
                  <div className="instructor-content">
                    {formation.instructorPhoto && (
                      <div className="instructor-photo" style={{ position: 'relative' }}>
                        <img
                          src={formation.instructorPhoto}
                          alt={formation.instructor || 'Formateur'}
                          style={
                            formation.instructorPhotoSettings
                              ? {
                                  transform: `translate(calc(-50% + ${formation.instructorPhotoSettings.position.x}px), calc(-50% + ${formation.instructorPhotoSettings.position.y}px)) scale(${formation.instructorPhotoSettings.scale})`,
                                  position: 'absolute',
                                  top: '50%',
                                  left: '50%',
                                  minWidth: '100%',
                                  minHeight: '100%',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'cover',
                                }
                              : undefined
                          }
                        />
                      </div>
                    )}
                    <div className="instructor-info">
                      {formation.instructor && (
                        <span className="instructor-name">{formation.instructor}</span>
                      )}
                      {formation.instructorTitle && (
                        <span className="instructor-title">{formation.instructorTitle}</span>
                      )}
                      {formation.instructorBio && (
                        <p className="instructor-bio">{formation.instructorBio}</p>
                      )}
                      {formation.instructorCV && (
                        <a
                          href={formation.instructorCV}
                          className="btn-instructor-cv"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <i className="fas fa-file-pdf" aria-hidden="true" />
                          Voir le CV du formateur
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="formation-content-detailed">
                <p className="formation-description">{formation.description}</p>

                <div className="formation-objectives">
                  <h4>
                    <i className="fas fa-bullseye" /> Objectifs Principaux
                  </h4>
                  <ul className="objectives-list">
                    {(formation.objectives || []).map((objective, objIndex) => (
                      <li key={objIndex}>
                        <i className="fas fa-check-circle" />
                        <span>{objective}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {(formation.program || []).length > 0 && (
                  <div className="formation-program">
                    <h4>
                      <i className="fas fa-list-ol" /> Programme de Formation
                    </h4>
                    <div className="program-modules">
                      {formation.program.map((module, moduleIndex) => (
                        <div key={moduleIndex} className="program-module">
                          <div className="module-header">
                            {module.module && <span className="module-name">{module.module}</span>}
                            {module.duration && <span className="module-duration">{module.duration}</span>}
                          </div>
                          {module.title && <h5>{module.title}</h5>}
                          {(module.content || []).length > 0 && (
                            <ul className="module-content">
                              {module.content.map((item, contentIndex) => (
                                <li key={contentIndex}>{item}</li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {formation.brochure && (
                  <div className="formation-brochure" style={{ margin: '1.5rem 0' }}>
                    <a
                      href={formation.brochure}
                      className="btn btn-outline"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fas fa-download" aria-hidden="true" />
                      Télécharger la brochure
                    </a>
                  </div>
                )}

                <div className="formation-sessions">
                  <h4>
                    <i className="fas fa-calendar-alt" /> Prochaines Sessions
                  </h4>
                  <div className="sessions-list">
                    {upcomingSessions.map((session, sessionIndex) => (
                      <div key={sessionIndex} className="session-item">
                        <div className="session-date">
                          <i className="fas fa-calendar" />
                          <strong>{session.date}</strong>
                        </div>
                        <div className="session-location">
                          <i className="fas fa-map-marker-alt" />
                          <span>{session.location}</span>
                        </div>
                        <div className="session-spots">
                          <span className="spots-badge">{session.spots}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="formation-reviews" id="avis">
                  <h4>
                    <i className="fas fa-star" /> Avis des Participants
                  </h4>
                  <div className="reviews-list">
                    {getFormationReviewsDisplay(formation, getContent).map((review, reviewIndex) => (
                      <div key={`${review.source}-${reviewIndex}`} className="review-item">
                        <div className="review-header">
                          <span className="reviewer-name">{review.name}</span>
                          <div className="review-rating">
                            {[...Array(5)].map((_, starIndex) => (
                              <i
                                key={starIndex}
                                className={`fas fa-star ${starIndex < review.rating ? 'star-filled' : 'star-empty'}`}
                              />
                            ))}
                          </div>
                          <span className="review-date">{review.date}</span>
                        </div>
                        {isVideoReview(review) ? (
                          <TestimonialVideoBlock item={review} name={review.name} />
                        ) : (
                          <p className="review-comment">&ldquo;{review.comment}&rdquo;</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="registration-section formation-detail-registration" id="inscription">
        <div className="container">
          <div className="registration-container">
            <div className="registration-header">
              <h2 id="demande-inscription-heading">
                {registrationForm.title || "Demande d'Inscription"}
              </h2>
              <p>
                {registrationForm.subtitle ||
                  "Remplissez ce formulaire pour recevoir plus d'informations sur nos formations"}
              </p>
            </div>

            {formStatus.message && (
              <div className={`form-message ${formStatus.type}`}>
                <i
                  className={`fas fa-${formStatus.type === 'success' ? 'check-circle' : 'exclamation-circle'}`}
                />
                {formStatus.message}
              </div>
            )}

            <form onSubmit={handleSubmit} className="registration-form">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nom">
                    <i className="fas fa-user" />
                    Nom complet <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="text"
                    id="nom"
                    name="nom"
                    value={formData.nom}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email">
                    <i className="fas fa-envelope" />
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="telephone">
                    <i className="fas fa-phone" />
                    Téléphone <span style={{ color: '#dc3545' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    id="telephone"
                    name="telephone"
                    value={formData.telephone}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <CountryCityFields
                  contactForm={contactForm}
                  renderCity={false}
                  countryName="country"
                  countryId="country"
                  countryValue={formData.country || selectedCountry}
                  onCountryChange={(value) => {
                    setSelectedCountry(value);
                    setFormData((prev) => ({ ...prev, country: value, ville: '' }));
                    setCityKey((k) => k + 1);
                  }}
                />
              </div>

              <CountryCityFields
                contactForm={contactForm}
                renderCountry={false}
                cityName="ville"
                cityId="ville"
                cityValue={formData.ville}
                onCityChange={(value) => setFormData((prev) => ({ ...prev, ville: value }))}
                activeCountry={formData.country || selectedCountry}
                cityResetKey={cityKey}
              />

              <div className="form-group">
                <label htmlFor="formation">
                  <i className="fas fa-graduation-cap" />
                  Formation d&apos;intérêt
                </label>
                <FormationSelect
                  getContent={getContent}
                  id="formation"
                  name="formation"
                  value={formData.formation}
                  onChange={handleInputChange}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="message">
                  <i className="fas fa-comment" />
                  Message (optionnel)
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Parlez-nous de vos besoins et objectifs..."
                />
              </div>

              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <i className="fas fa-spinner fa-spin" />
                    Envoi en cours...
                  </>
                ) : (
                  <>
                    <i className={registrationForm.submitButton?.icon || 'fas fa-paper-plane'} />
                    {registrationForm.submitButton?.text || "S'inscrire à cette Formation"}
                  </>
                )}
              </button>

              {registrationForm.securityNote?.text && (
                <p className="form-security-note" style={{ marginTop: '1rem', fontSize: '0.85rem', color: '#6b7280' }}>
                  <i className={registrationForm.securityNote?.icon || 'fas fa-lock'} /> {registrationForm.securityNote.text}
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FormationDetail;
